/**
 * Resolves release dates for an asset page without exceeding the campaign-titles API's
 * per-request GTI limit or exposing cached results from a previous filter context.
 */
import { chunk } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FEATURE_FLAGS } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';
import type { AppDispatch } from 'src/store';
import type { CampaignTitlesControllerGetCampaignTitlesApiArg } from 'src/store/generated/leela-api-generated';
import { leelaApi } from 'src/store/leela-api';
import { epochToIsoDate } from 'src/utils/releaseDate';

export const CAMPAIGN_TITLE_GTI_LIMIT = 500;

const EMPTY_RELEASE_DATES = new Map<string, string | undefined>();

interface CampaignTitleReleaseDateOptions {
  readonly countryCode?: CampaignTitlesControllerGetCampaignTitlesApiArg['countryCode'];
  readonly gtis: readonly string[];
  readonly locale?: CampaignTitlesControllerGetCampaignTitlesApiArg['locale'];
}

interface CampaignTitleReleaseDateResult {
  readonly isFetching: boolean;
  readonly releaseDatesByGti: ReadonlyMap<string, string | undefined>;
}

interface CampaignTitleReleaseDateState extends CampaignTitleReleaseDateResult {
  readonly requestKey: string;
}

export function useCampaignTitleReleaseDates ({
  countryCode,
  gtis,
  locale,
}: CampaignTitleReleaseDateOptions): CampaignTitleReleaseDateResult {
  const isReleaseDateEnabled = isFeatureEnabled(
    FEATURE_FLAGS.ENABLE_ASSET_IMPROVEMENTS,
  );
  const dispatch = useDispatch<AppDispatch>();
  const queryArgs = useMemo(
    () => {
      if (!isReleaseDateEnabled) return [];
      return chunk(Array.from(gtis), CAMPAIGN_TITLE_GTI_LIMIT).map((gtiList) => ({
        gtiList,
        ...(countryCode && { countryCode }),
        ...(locale && { locale }),
      }));
    },
    [countryCode, gtis, isReleaseDateEnabled, locale],
  );
  const requestKey = useMemo(() => JSON.stringify(queryArgs), [queryArgs]);
  const [state, setState] = useState<CampaignTitleReleaseDateState>({
    isFetching: false,
    releaseDatesByGti: EMPTY_RELEASE_DATES,
    requestKey: '',
  });

  useEffect(() => {
    if (queryArgs.length === 0) {
      setState({
        isFetching: false,
        releaseDatesByGti: EMPTY_RELEASE_DATES,
        requestKey,
      });
      return;
    }

    let isActive = true;
    setState({
      isFetching: true,
      releaseDatesByGti: EMPTY_RELEASE_DATES,
      requestKey,
    });
    const subscriptions = queryArgs.map((args) =>
      dispatch(
        leelaApi.endpoints.campaignTitlesControllerGetCampaignTitles.initiate(
          args,
        ),
      ),
    );

    Promise.all(
      subscriptions.map(async (subscription) => {
        try {
          return await subscription.unwrap();
        } catch {
          return [];
        }
      }),
    ).then((responses) => {
      if (!isActive) return;
      setState({
        isFetching: false,
        releaseDatesByGti: new Map(
          responses
            .flat()
            .map((title) => [
              title.gti,
              epochToIsoDate(title.publicReleaseDate),
            ]),
        ),
        requestKey,
      });
    });

    return () => {
      isActive = false;
      subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
    };
  }, [dispatch, queryArgs, requestKey]);

  if (state.requestKey !== requestKey) {
    return {
      isFetching: queryArgs.length > 0,
      releaseDatesByGti: EMPTY_RELEASE_DATES,
    };
  }
  return state;
}
